import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { verifyOtp } from '../../services/authService';

const VerifyOtpPage: React.FC = () => {
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const navigate = useNavigate();
  const email = localStorage.getItem('resetEmail');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    if (/^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>,
  ) => {
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement | HTMLDivElement>) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData('text/plain').trim();
    if (/^\d{6}$/.test(pasteData)) {
      const pasteArray = pasteData.split('');
      setOtp(pasteArray.slice(0, 6));
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const otpString = otp.join('');

    if (otpString.length !== 6) {
      setError('Please enter a 6-digit verification code.');
      return;
    }

    setLoading(true);
    try {
      await verifyOtp(email!, otpString);
      navigate('/reset-password');
    } catch (err: any) {
      setError(err.response?.data?.msg || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 6,
        background:
          'radial-gradient(circle at top, rgba(90, 75, 252, 0.12), transparent 40%), radial-gradient(circle at bottom, rgba(0, 184, 148, 0.16), transparent 45%), #f4f6fb',
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 520,
          borderRadius: 3,
          boxShadow: '0 36px 60px rgba(15, 23, 42, 0.16)',
        }}
      >
        <CardContent sx={{ p: { xs: 4, md: 5 } }}>
          <Stack spacing={4} component="form" onSubmit={handleSubmit}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Verify the code
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                We sent a 6-digit verification code to <strong>{email ?? 'your email'}</strong>. Enter it below to continue.
              </Typography>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" spacing={1.5} justifyContent="space-between">
              {otp.map((digit, index) => (
                <TextField
                  key={index}
                  type={showOtp ? 'text' : 'password'}
                  value={digit}
                  onChange={(event) => handleChange(index, event.target.value)}
                  onKeyDown={(event) => handleKeyDown(index, event)}
                  onPaste={handlePaste}
                  inputRef={(element) => {
                    inputRefs.current[index] = element;
                  }}
                  required
                  inputProps={{
                    maxLength: 1,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    style: {
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      padding: '0.6rem 0',
                    },
                  }}
                  sx={{
                    width: { xs: 48, sm: 56 },
                  }}
                />
              ))}
            </Stack>

            <FormControlLabel
              control={
                <Checkbox
                  checked={showOtp}
                  onChange={() => setShowOtp((prev) => !prev)}
                />
              }
              label="Show code"
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
            >
              {loading ? 'Verifying…' : 'Verify code'}
            </Button>

            <Typography variant="body2" color="text.secondary" align="center">
              Didn’t receive it?{' '}
              <Link component={RouterLink} to="/forgot-password" underline="hover">
                Resend code
              </Link>
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VerifyOtpPage;
